import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, Button, IconButton, Tooltip, CircularProgress, Alert, Snackbar, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material'
import { Undo, DeleteOutline, VisibilityOff, Visibility, Close } from '@mui/icons-material'
import {
  splitModifications, ruleTitle, ruleScopeLabel, keptEdits, cellGroups, panelCounts, formatDelta,
} from './modificationsModel'

const GREEN = '#15803D'
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString())

// FAR-78: the Modifications panel. Every action saves at once (§2) -- there is
// no Save button. Rules are made on the board (Apply Modification, FAR-76 §5),
// so the panel has no rule builder; it lists Rules newest first (the newest
// wins where two overlap) and the Units the planner typed (Cell edits).
//
// The Planning Board owns the list and does the saving; a failed save is
// undone there and shown here as saveError.
export default function ModificationsPanel({
  modifications, loading, saveError, listName, rows, baseRowsByItem, months = [],
  onRemove, onRestore, onToggleIgnore, onRevertCell, onRevertSku, onRevertAll, readOnly,
  review = [], onBackToBasePlan,
}) {
  const [undo, setUndo] = useState(null) // { mod } -- the last removed rule
  const [undoError, setUndoError] = useState(null)
  const [confirmRevertAll, setConfirmRevertAll] = useState(false)

  const { rules, cells } = useMemo(() => splitModifications(modifications), [modifications])
  const counts = useMemo(() => panelCounts(modifications), [modifications])
  const keyOf = useMemo(() => {
    const m = Object.fromEntries(months.map((x) => [x.iso, x]))
    return (iso) => m[iso]?.key
  }, [months])
  const labelOf = (iso) => months.find((m) => m.iso === iso)?.label || iso
  const groups = useMemo(() => cellGroups(cells, rows, baseRowsByItem, keyOf), [cells, rows, baseRowsByItem, keyOf])

  const remove = async (mod) => {
    const ok = await onRemove(mod.id)
    if (ok !== false) setUndo({ mod })
  }

  // Review of #72: say so when Undo fails -- the rule is still removed.
  const undoRemove = async () => {
    const m = undo.mod
    setUndo(null)
    setUndoError(null)
    const ok = await onRestore(m)
    if (ok === false) setUndoError(`Couldn't restore "${ruleTitle(m)}". It is still removed.`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="modifications-panel">
      {/* §2 header */}
      <Box>
        {listName && (
          <Typography variant="caption" color="text.secondary">Started from {listName}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
          <Chip size="small" label={`${counts.rules} rule${counts.rules === 1 ? '' : 's'}`} data-testid="chip-rules" />
          <Chip size="small" label={`${counts.edits} cell edit${counts.edits === 1 ? '' : 's'}`} data-testid="chip-edits"
            sx={{ bgcolor: '#DCFCE7', color: GREEN }} />
          {counts.ignored > 0 && <Chip size="small" variant="outlined" label={`${counts.ignored} ignored`} />}
          {review.length > 0 && (
            <Chip size="small" label={`${review.length} need review`} data-testid="chip-review"
              sx={{ bgcolor: '#FEF3C7', color: '#92400E' }} />
          )}
          {loading && <CircularProgress size={14} />}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Changes save automatically.
        </Typography>
      </Box>

      {saveError && <Alert severity="error">{saveError}</Alert>}

      {/* FAR-78 §3–4: typed edits made against another Base Plan that now look wrong. */}
      {review.length > 0 && (
        <Box data-testid="review-box" sx={{ border: '1px solid #FCD34D', bgcolor: '#FFFBEB', borderRadius: 1.5, p: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, color: '#92400E' }}>
              {review.length} edit{review.length === 1 ? '' : 's'} need review · Base Plan changed since typed
            </Typography>
            {!readOnly && (
              <Button size="small" onClick={onBackToBasePlan} data-testid="back-to-base-plan">Back to Base Plan</Button>
            )}
          </Box>
          {review.map((it) => (
            <Box key={`${it.itemNumber}-${it.iso}`} sx={{ display: 'flex', gap: 1, fontSize: '0.75rem', py: 0.25 }}>
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <b>{it.description || it.itemNumber}</b> · {it.month}
              </Box>
              <Box sx={{ whiteSpace: 'nowrap' }}>{num(it.typed)} → {num(it.basePlan)}</Box>
              <Box sx={{ color: '#92400E', whiteSpace: 'nowrap' }}>{it.reason}</Box>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            Back to Base Plan removes only these typed months. Rules and other edits stay.
          </Typography>
        </Box>
      )}
      {undoError && <Alert severity="error" onClose={() => setUndoError(null)} data-testid="undo-error">{undoError}</Alert>}
      {readOnly && <Alert severity="info">This plan is approved, so its modifications can't be changed.</Alert>}

      {/* §3–5 Rules */}
      <Box>
        <Typography variant="overline" color="text.secondary">Rules · newest first</Typography>
        {rules.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No rules yet. Check SKUs on the board and use Apply Modification.
          </Typography>
        ) : rules.map((r, i) => {
          const kept = keptEdits(r, cells, rows)
          return (
            <Box key={r.id} data-testid={`rule-${r.id}`} sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25, mb: 1,
              borderLeft: `4px solid ${r.isIgnored ? '#9CA3AF' : GREEN}`,
              opacity: r.isIgnored ? 0.6 : 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, textDecoration: r.isIgnored ? 'line-through' : 'none' }}>
                  {ruleTitle(r)}
                </Typography>
                {i === 0 && rules.length > 1 && !r.isIgnored && (
                  <Tooltip title="Where rules overlap, the newest wins"><Chip size="small" label="Newest" sx={{ height: 18, fontSize: '0.65rem' }} /></Tooltip>
                )}
                {r.isIgnored && <Chip size="small" label="Ignored" sx={{ height: 18, fontSize: '0.65rem' }} />}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {ruleScopeLabel(r, rows)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {[r.createdBy, r.createdAt && new Date(r.createdAt).toLocaleString()].filter(Boolean).join(' · ')}
                {kept > 0 && <> · <Box component="span" sx={{ color: GREEN }}>kept {kept} typed edit{kept === 1 ? '' : 's'}</Box></>}
              </Typography>
              {!readOnly && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button size="small" startIcon={r.isIgnored ? <Visibility /> : <VisibilityOff />}
                    onClick={() => onToggleIgnore(r.id)}>
                    {r.isIgnored ? 'Include' : 'Ignore'}
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => remove(r)}>
                    Remove
                  </Button>
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      <Divider />

      {/* §6 Cell edits */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>Cell edits</Typography>
          {!readOnly && groups.length > 0 && (
            <Button size="small" startIcon={<Undo />} onClick={() => setConfirmRevertAll(true)}>Revert all</Button>
          )}
        </Box>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No typed Units. Click a Units cell on the board to edit it.
          </Typography>
        ) : groups.map((g) => (
          <Box key={g.itemNumber} data-testid={`cells-${g.itemNumber}`} sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                {g.description || g.itemNumber}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{g.itemNumber}</Typography>
              </Typography>
              {!readOnly && (
                <Button size="small" startIcon={<Undo />} onClick={() => onRevertSku(g.itemNumber)}>Revert SKU</Button>
              )}
            </Box>
            {g.edits.map((e) => (
              <Box key={e.iso} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                <Typography variant="caption" sx={{ width: 64 }}>{labelOf(e.iso)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Base {num(e.base)} → <Box component="span" sx={{ color: GREEN, fontWeight: 700 }}>{num(e.units)}</Box>
                  {e.delta != null && (
                    <Box component="span" sx={{ ml: 1 }}>({formatDelta(e.delta)})</Box>
                  )}
                </Typography>
                {!readOnly && (
                  <Tooltip title="Revert this month">
                    <IconButton size="small" aria-label={`Revert ${g.itemNumber} ${labelOf(e.iso)}`}
                      onClick={() => onRevertCell(g.mod, e.iso)}>
                      <Undo sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {/* Review of #72: a dialog, not window.confirm (which can be blocked). */}
      <Dialog open={confirmRevertAll} onClose={() => setConfirmRevertAll(false)}>
        <DialogTitle>Revert all cell edits?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All {counts.edits} typed Units go back to the Base Plan. Rules are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevertAll(false)}>Cancel</Button>
          <Button color="error" variant="contained" data-testid="confirm-revert-all"
            onClick={() => { setConfirmRevertAll(false); onRevertAll() }}>Revert all</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!undo}
        autoHideDuration={8000}
        // Clicking elsewhere (e.g. the board behind the drawer) mustn't throw
        // the Undo away; only the timeout or the close button does.
        onClose={(_e, reason) => { if (reason !== 'clickaway') setUndo(null) }}
        message={undo ? `Removed "${ruleTitle(undo.mod)}"` : ''}
        action={<>
          <Button size="small" color="inherit" onClick={undoRemove}>Undo</Button>
          <IconButton size="small" color="inherit" aria-label="Dismiss" onClick={() => setUndo(null)}><Close fontSize="small" /></IconButton>
        </>}
      />
    </Box>
  )
}
